import React, { useEffect, useState } from 'react';
import './holidayTerms.css';
import usePageMeta from "../../../hooks/usePageMeta";

const holidayTerms = [
  {
    number: 1,
    title: 'Holiday Package Services',
    paragraphs: [
      'MakeBeeTrip provides domestic and international holiday packages that may include a combination of:',
    ],
    bullets: [
      'Accommodation',
      'Airport or railway station transfers',
      'Local transportation',
      'Sightseeing',
      'Meals',
      'Activities and excursions',
      'Tour services',
      'Guide services',
      'Other travel arrangements mentioned in the package itinerary',
    ],
    after: [
      'The exact inclusions will depend on the specific package, quotation and booking confirmation.',
    ],
  },

  {
    number: 2,
    title: 'Package Confirmation',
    paragraphs: [
      'A holiday package is considered confirmed only after the required payment has been received and the relevant services have been confirmed by the applicable suppliers.',
      'A payment receipt alone does not necessarily mean that all package components are confirmed.',
      'Customers should rely on the final booking confirmation/voucher/itinerary issued by MakeBeeTrip.',
    ],
  },

  {
    number: 3,
    title: 'Package Price',
    paragraphs: [
      'Holiday package prices are based on the details provided at the time of quotation or booking.',
      'The final price may depend on:',
    ],
    bullets: [
      'Number of travellers',
      'Travel dates',
      'Hotel category',
      'Room occupancy',
      'Meal plan',
      'Transportation',
      'Sightseeing',
      'Activities',
      'Flight availability',
      'Seasonal pricing',
      'Supplier rates',
      'Taxes and government charges',
    ],
    after: [
      'Prices may change before confirmation if supplier rates or availability change.',
    ],
  },

  {
    number: 4,
    title: 'Package Inclusions',
    paragraphs: [
      'The services included in a holiday package will be specified in the applicable quotation, itinerary or booking confirmation.',
      'Only services specifically mentioned as Included will be considered part of the package.',
      'Anything not specifically mentioned as included should be considered excluded.',
    ],
  },

  {
    number: 5,
    title: 'Package Exclusions',
    paragraphs: [
      'Unless specifically mentioned as included, the following may be excluded:',
    ],
    bullets: [
      'Airfare',
      'Visa fees',
      'Travel insurance',
      'Personal expenses',
      'Meals not mentioned in the itinerary',
      'Tips and gratuities',
      'Laundry',
      'Room service',
      'Additional sightseeing',
      'Entry tickets',
      'Camera/video charges',
      'Optional activities',
      'Personal transportation',
      'Additional hotel charges',
      'Any other service not specifically mentioned in the package',
    ],
    after: [
      'Customers should carefully review the inclusions and exclusions before confirming the package.',
    ],
  },

  {
    number: 6,
    title: 'Hotel Accommodation',
    paragraphs: [
      'Hotel accommodation is subject to availability and confirmation by the relevant hotel or supplier.',
      'The hotel provided may be the selected property or a similar property where the package specifically allows a "similar hotel" or equivalent accommodation.',
      'Hotel policies regarding check-in, check-out, child occupancy, meals, security deposits and other services will apply.',
    ],
  },

  {
    number: 7,
    title: 'Room Occupancy',
    paragraphs: [
      'Package pricing is generally calculated according to the room occupancy mentioned in the quotation.',
      'Changes in the number of adults or children may result in a change in package price.',
      'Extra beds, child accommodation or additional occupancy are subject to hotel availability and applicable charges.',
    ],
  },

  {
    number: 8,
    title: 'Transportation',
    paragraphs: [
      'Transportation included in a package will be provided according to the itinerary and booking confirmation.',
      'Vehicle type may depend on:',
    ],
    bullets: [
      'Number of travellers',
      'Destination',
      'Availability',
      'Route',
      'Local transportation rules',
      'Package inclusions',
    ],
    after: [
      'The vehicle may be changed to an equivalent vehicle where necessary due to operational requirements or availability.',
    ],
  },

  {
    number: 9,
    title: 'Sightseeing',
    paragraphs: [
      'Sightseeing included in the package will be as specified in the confirmed itinerary.',
      'Sightseeing may be subject to:',
    ],
    bullets: [
      'Weather conditions',
      'Local operating hours',
      'Entry restrictions',
      'Government regulations',
      'Attraction closures',
      'Traffic conditions',
      'Local events',
      'Operational circumstances',
    ],
    after: [
      'If a sightseeing location is closed or inaccessible, an alternative may be offered where reasonably possible.',
    ],
  },

  {
    number: 10,
    title: 'Itinerary Changes',
    paragraphs: [
      'The itinerary provided at the time of booking may be changed due to operational, weather, safety, local or supplier-related circumstances.',
      'Changes may include:',
    ],
    bullets: [
      'Change in sightseeing sequence',
      'Change in transportation',
      'Change in hotel',
      'Change in activity timing',
      'Change in route',
    ],
    after: [
      'MakeBeeTrip will make reasonable efforts to maintain the overall package as confirmed.',
    ],
  },

  {
    number: 11,
    title: 'Flights Included in Holiday Packages',
    paragraphs: [
      "Where flights are included in a holiday package, the flight booking is subject to the applicable airline's:",
    ],
    bullets: [
      'Fare rules',
      'Baggage allowance',
      'Cancellation policy',
      'Amendment policy',
      'Schedule',
      'Operational conditions',
    ],
    after: [
      "Airline schedule changes, delays and cancellations are governed by the airline's applicable policies.",
    ],
  },

  {
    number: 12,
    title: 'Meals',
    paragraphs: [
      'Meals included in the package will be specified in the itinerary or booking confirmation.',
      'Meal plans may include:',
    ],
    bullets: [
      'Breakfast',
      'Lunch',
      'Dinner',
      'Half Board',
      'Full Board',
      'Other specified meal arrangements',
    ],
    after: [
      "Meals not specifically mentioned as included will be at the customer's own expense.",
      'Meal timings are determined by the hotel or service provider.',
    ],
  },

  {
    number: 13,
    title: 'Activities and Excursions',
    paragraphs: [
      'Activities included in a holiday package are subject to availability and the operating conditions of the relevant provider.',
      'Certain activities may have age, health, safety, weather or other restrictions.',
      'Customers must comply with the instructions and safety requirements of the activity provider.',
    ],
  },

  {
    number: 14,
    title: 'Travel Documents',
    paragraphs: [
      'Customers are responsible for carrying all required travel documents, including where applicable:',
    ],
    bullets: [
      'Passport',
      'Visa',
      'Travel insurance',
      'Tickets',
      'Booking confirmations',
      'Entry permits',
      'Other destination-specific documents',
    ],
    after: [
      'MakeBeeTrip is not responsible for denied boarding or entry resulting from missing, incorrect or invalid travel documents.',
    ],
  },

  {
    number: 15,
    title: 'Visa',
    paragraphs: [
      'Visa assistance may be available for selected destinations.',
      'Visa approval is solely at the discretion of the relevant embassy, consulate or immigration authority.',
      'MakeBeeTrip does not guarantee visa approval or processing within a particular timeframe.',
      'Customers are responsible for providing accurate and genuine documents.',
    ],
  },

  {
    number: 16,
    title: 'Cancellation of Holiday Package',
    paragraphs: [
      'Cancellation of a holiday package is subject to the cancellation terms mentioned in the applicable quotation, booking confirmation or package terms.',
      'Because a holiday package may contain multiple services, cancellation charges may be imposed by individual suppliers.',
      'Applicable charges may include:',
    ],
    bullets: [
      'Hotel cancellation charges',
      'Flight cancellation charges',
      'Transportation charges',
      'Activity cancellation charges',
      'Visa charges',
      'Supplier penalties',
      'MakeBeeTrip service charges',
      'Other non-refundable amounts',
    ],
    after: [
      'The final refundable amount will depend on the applicable booking conditions.',
    ],
  },

  {
    number: 17,
    title: 'Non-Refundable Components',
    paragraphs: [
      'Certain components of a holiday package may be non-refundable.',
      'These may include:',
    ],
    bullets: [
      'Non-refundable hotel bookings',
      'Promotional flight fares',
      'Visa fees',
      'Activity bookings',
      'Special transportation arrangements',
      'Supplier deposits',
      'Other non-refundable services',
    ],
    after: [
      'Non-refundable amounts will be deducted from any applicable refund.',
    ],
  },

  {
    number: 18,
    title: 'Booking Amendments',
    paragraphs: [
      'Requests to change a confirmed holiday package are subject to availability and supplier approval.',
      'Changes may include:',
    ],
    bullets: [
      'Travel dates',
      'Number of travellers',
      'Hotel',
      'Room category',
      'Transportation',
      'Sightseeing',
      'Activities',
      'Meal plan',
      'Itinerary',
    ],
    after: [
      'Additional charges or price differences may apply.',
      'Some confirmed services may not be amendable or refundable.',
    ],
  },

  {
    number: 19,
    title: 'Customer-Initiated Changes',
    paragraphs: [
      'If a customer requests changes after confirmation, any additional cost resulting from the change will generally be payable by the customer.',
      'This may include:',
    ],
    bullets: [
      'Fare differences',
      'Hotel rate differences',
      'Transportation charges',
      'Supplier amendment fees',
      'Cancellation penalties',
      'Additional service charges',
    ],
  },

  {
    number: 20,
    title: 'No-Show',
    paragraphs: [
      'If a customer does not use a confirmed service without cancelling according to the applicable cancellation policy, it may be treated as a No-Show.',
      'No-show charges may apply and some or all components of the package may become non-refundable.',
    ],
  },

  {
    number: 21,
    title: 'Supplier Changes',
    paragraphs: [
      'Hotels, airlines, transportation providers, activity operators or other suppliers may sometimes change or discontinue a service due to operational reasons.',
      'Where this happens, MakeBeeTrip will make reasonable efforts to provide an alternative arrangement of comparable nature, subject to availability and supplier policies.',
      'Additional charges may apply where the customer requests an upgrade or alternative service costing more than the original arrangement.',
    ],
  },

  {
    number: 22,
    title: 'Travel During Peak Periods',
    paragraphs: [
      'Holiday packages during peak seasons, festivals, public holidays, school holidays and major events may be subject to higher prices and limited availability.',
      'Special cancellation and payment conditions may also apply.',
      'These conditions will be communicated where applicable.',
    ],
  },

  {
    number: 23,
    title: 'Personal Expenses',
    paragraphs: [
      'Unless specifically included in the package, customers are responsible for their personal expenses, including:',
    ],
    bullets: [
      'Shopping',
      'Laundry',
      'Room service',
      'Telephone calls',
      'Minibar',
      'Tips',
      'Personal meals',
      'Optional activities',
      'Additional transportation',
      'Additional sightseeing',
      'Other personal expenses',
    ],
  },

  {
    number: 24,
    title: 'Travel Insurance',
    paragraphs: [
      'Travel insurance is recommended for domestic and international travel.',
      'Unless specifically mentioned in the package, travel insurance is not included.',
      'Customers are responsible for arranging suitable insurance coverage according to their travel requirements.',
    ],
  },

  {
    number: 25,
    title: 'Force Majeure',
    paragraphs: [
      'MakeBeeTrip shall not be responsible for delays, cancellations or changes caused by circumstances beyond reasonable control, including:',
    ],
    bullets: [
      'Natural disasters',
      'Severe weather',
      'Government restrictions',
      'Strikes',
      'Civil disturbances',
      'War or security situations',
      'Pandemics or public health emergencies',
      'Transportation disruptions',
      'Airline or supplier operational issues',
      'Local restrictions',
      'Other unforeseen circumstances',
    ],
  },

  {
    number: 26,
    title: 'Customer Responsibilities',
    paragraphs: [
      'Customers are responsible for:',
    ],
    bullets: [
      'Providing accurate booking information',
      'Checking the confirmed itinerary',
      'Carrying required travel documents',
      'Following local laws and regulations',
      'Following hotel and supplier rules',
      'Arriving at scheduled pickup and departure points on time',
      'Paying applicable additional charges',
      'Informing MakeBeeTrip of relevant changes to their requirements',
    ],
  },

  {
    number: 27,
    title: 'Limitation of Responsibility',
    paragraphs: [
      'MakeBeeTrip acts as a travel platform and facilitator for holiday package services involving various third-party suppliers.',
      'To the extent permitted by applicable law, MakeBeeTrip shall not be responsible for circumstances caused by third-party suppliers or events beyond its reasonable control.',
      'MakeBeeTrip will make reasonable efforts to assist customers with issues relating to their confirmed package.',
    ],
  },

  {
    number: 28,
    title: 'Package-Specific Terms',
    paragraphs: [
      'Each holiday package may have additional terms relating to:',
    ],
    bullets: [
      'Payment schedules',
      'Cancellation',
      'Refunds',
      'Hotels',
      'Flights',
      'Transportation',
      'Activities',
      'Visa',
      'Special inclusions',
      'Peak-season conditions',
    ],
    after: [
      'Where package-specific terms are provided in the quotation or booking confirmation, those terms will apply to the relevant booking.',
    ],
  },

  {
    number: 29,
    title: 'Changes to Holiday Package Terms',
    paragraphs: [
      'MakeBeeTrip may update these Holiday Package Terms & Conditions from time to time to reflect changes in our services, supplier requirements or applicable regulations.',
      'The updated version will be published on this page with a revised "Last Updated" date.',
    ],
  },
];

/* ============================================================
   Section Renderer
   ============================================================ */

function renderParagraph(text, sectionNumber, index) {
  if (sectionNumber === 4 && index === 1) {
    const parts = text.split('Included');

    return (
      <>
        {parts[0]}
        <strong>Included</strong>
        {parts[1]}
      </>
    );
  }

  if (sectionNumber === 20 && index === 0) {
    const parts = text.split('No-Show');

    return (
      <>
        {parts[0]}
        <strong>No-Show</strong>
        {parts[1]}
      </>
    );
  }

  if (sectionNumber === 29 && index === 1) {
    const parts = text.split('"Last Updated"');

    return (
      <>
        {parts[0]}
        <strong>"Last Updated"</strong>
        {parts[1]}
      </>
    );
  }

  return text;
}

function TermsSection({ section }) {
  return (
    <section
      id={`holiday-section-${section.number}`}
      className="mbt-holiday-section"
    >
      <div className="mbt-holiday-section__heading">
        <span className="mbt-holiday-section__number">
          {String(section.number).padStart(2, '0')}
        </span>

        <h2>{section.title}</h2>
      </div>

      <div className="mbt-holiday-section__content">
        {section.paragraphs?.map((paragraph, index) => (
          <p key={`paragraph-${section.number}-${index}`}>
            {renderParagraph(paragraph, section.number, index)}
          </p>
        ))}

        {section.bullets && (
          <ul>
            {section.bullets.map((bullet, index) => (
              <li key={`bullet-${section.number}-${index}`}>
                {bullet}
              </li>
            ))}
          </ul>
        )}

        {section.after?.map((paragraph, index) => (
          <p key={`after-${section.number}-${index}`}>
            {paragraph}
          </p>
        ))}
      </div>
    </section>
  );
}

/* ============================================================
   Page
   ============================================================ */

export default function HolidayTerms() {
  usePageMeta(
  "Holiday Package Terms & Conditions | MakeBeeTrip",
  "Terms for holiday package bookings on MakeBeeTrip: inclusions, itineraries, payment schedules, amendments and cancellations.",
  "holiday package terms, tour package terms and conditions, package cancellation, itinerary changes, holiday booking terms"
);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;

      const documentHeight =
        document.documentElement.scrollHeight -
        window.innerHeight;

      const progress =
        documentHeight > 0
          ? (scrollTop / documentHeight) * 100
          : 0;

      setScrollProgress(Math.min(progress, 100));
      setShowScrollTop(scrollTop > 500);
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <main className="mbt-holiday-page">

      {/* Scroll Progress */}
      <div
        className="mbt-holiday-scroll-indicator"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      {/* Sticky Navigation */}
      <nav className="mbt-holiday-sticky-nav">
        <div className="mbt-holiday-nav-container">

          <button
            type="button"
            className="mbt-holiday-back-button"
            onClick={() => window.history.back()}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>

            Back
          </button>

          <span className="mbt-holiday-nav-label">
            Holiday Package Terms
          </span>

        </div>
      </nav>

      {/* Hero */}
      <header className="mbt-holiday-hero">
        <div className="mbt-holiday-hero__container">

          {/* Holiday Icon */}
          <div
            className="mbt-holiday-icon"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 8.5h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z" />
              <path d="M9 8.5V6.8A1.8 1.8 0 0 1 10.8 5h2.4A1.8 1.8 0 0 1 15 6.8v1.7" />
              <path d="M4 12.5h16" />
              <path d="M9 12.5v2M15 12.5v2" />
              <path d="M18.5 5.5c-.9.1-1.7.5-2.2 1.2-.5.6-.7 1.4-.6 2.1" />
              <path d="M18.5 5.5c-.1.9.2 1.7.8 2.3.6.6 1.4.9 2.2.8" />
            </svg>
          </div>

          <span className="mbt-holiday-eyebrow">
            SERVICE-SPECIFIC TERMS
          </span>

          <h1>
            Holiday Package Terms &amp; Conditions
          </h1>

          <p className="mbt-holiday-hero__description">
            Terms and conditions applicable to domestic and international
            holiday packages booked through MakeBeeTrip Private Limited.
          </p>

          <div className="mbt-holiday-updated-badge">
            Last Updated: 24 September 2026
          </div>

        </div>
      </header>

      {/* Main Document */}
      <div className="mbt-holiday-content">
        <article className="mbt-holiday-card">

          {/* Introduction */}
          <div className="mbt-holiday-intro">
            <p>
              These Holiday Package Terms &amp; Conditions apply to domestic
              and international holiday packages booked through{' '}
              <strong>MakeBeeTrip Private Limited</strong>.
            </p>

            <p>
              By making an enquiry, confirming a holiday package or making
              payment through{' '}
              <a
                href="https://makebeetrip.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://makebeetrip.com/
              </a>
              , you acknowledge that you have read and agreed to these terms.
            </p>
          </div>

          {/* Quick Navigation */}
          <nav
            className="mbt-holiday-quick-nav"
            aria-label="Holiday package terms sections"
          >
            <div className="mbt-holiday-quick-nav__header">
              <span className="mbt-holiday-quick-nav__eyebrow">
                QUICK NAVIGATION
              </span>

              <h2>Holiday Package Terms</h2>

              <p>
                Jump directly to any section of the Holiday Package Terms.
              </p>
            </div>

            <div className="mbt-holiday-quick-nav__links">

              {holidayTerms.map((section) => (
                <a
                  key={section.number}
                  href={`#holiday-section-${section.number}`}
                >
                  <span>
                    {String(section.number).padStart(2, '0')}
                  </span>

                  {section.title}
                </a>
              ))}

              <a href="#holiday-important-notice">
                <span>!</span>
                Important Notice
              </a>

            </div>
          </nav>

          {/* Sections */}
          <div className="mbt-holiday-sections">
            {holidayTerms.map((section, index) => (
              <React.Fragment key={section.number}>

                <TermsSection section={section} />

                {index < holidayTerms.length - 1 && (
                  <hr className="mbt-holiday-divider" />
                )}

              </React.Fragment>
            ))}
          </div>

          <hr className="mbt-holiday-divider" />

          {/* Important Notice */}
          <section
            id="holiday-important-notice"
            className="mbt-holiday-important-notice"
          >
            <div
              className="mbt-holiday-important-notice__icon"
              aria-hidden="true"
            >
              !
            </div>

            <div>
              <span>IMPORTANT NOTICE</span>

              <p>
                <strong>
                  Every holiday package is subject to the specific
                  inclusions, exclusions, payment conditions, cancellation
                  policy and supplier terms mentioned in the applicable
                  quotation and booking confirmation. Customers are advised
                  to carefully review all package details before making
                  payment.
                </strong>
              </p>
            </div>
          </section>

        </article>
      </div>

      {/* Footer */}
      <footer className="mbt-holiday-footer">
        <div className="mbt-holiday-footer__inner">

          <p className="mbt-holiday-footer__tagline">
            MakeBeeTrip – Your Journey,{' '}
            <span>Our Commitment</span>.
          </p>

          <div className="mbt-holiday-footer__contact">
            <a href="tel:9355471999">
              9355471999
            </a>

            <span aria-hidden="true">•</span>

            <a href="mailto:Holidays@makebeetrip.com">
              Holidays@makebeetrip.com
            </a>

            <span aria-hidden="true">•</span>

            <a
              href="https://makebeetrip.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              makebeetrip.com
            </a>
          </div>

          <p className="mbt-holiday-footer__copyright">
            © 2026 MakeBeeTrip Private Limited. All rights reserved.
          </p>

        </div>
      </footer>

      {/* Scroll To Top */}
      {showScrollTop && (
        <button
          type="button"
          className="mbt-holiday-scroll-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}

    </main>
  );
}